import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TrustSignals = () => {
  const certifications = [
    {
      id: 1,
      name: "ISO 27001",
      description: "Information Security Management",
      icon: "Shield",
      badge: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop"
    },
    {
      id: 2,
      name: "SOC 2 Type II",
      description: "Security & Availability",
      icon: "CheckCircle",
      badge: "https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=80&h=80&fit=crop"
    },
    {
      id: 3,
      name: "AWS Partner",
      description: "Advanced Consulting Partner",
      icon: "Cloud",
      badge: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=80&h=80&fit=crop"
    },
    {
      id: 4,
      name: "Microsoft Gold",
      description: "AI & Machine Learning",
      icon: "Award",
      badge: "https://images.unsplash.com/photo-1560472355-536de3962603?w=80&h=80&fit=crop"
    }
  ];

  const securityFeatures = [
    {
      icon: "Lock",
      title: "256-bit SSL Encryption",
      description: "Bank-grade security for all data transmission"
    },
    {
      icon: "Shield",
      title: "GDPR Compliant",
      description: "Full compliance with data protection regulations"
    },
    {
      icon: "Eye",
      title: "24/7 Monitoring",
      description: "Continuous security monitoring and threat detection"
    },
    {
      icon: "FileCheck",
      title: "Regular Audits",
      description: "Third-party security audits and compliance checks"
    }
  ];

  const clientLogos = [
    "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=120&h=40&fit=crop",
    "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=40&fit=crop",
    "https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=120&h=40&fit=crop",
    "https://images.unsplash.com/photo-1551434678-e076c223a692?w=120&h=40&fit=crop",
    "https://images.unsplash.com/photo-1560472355-536de3962603?w=120&h=40&fit=crop",
    "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=40&fit=crop",
    "https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=120&h=40&fit=crop",
    "https://images.unsplash.com/photo-1551434678-e076c223a692?w=120&h=40&fit=crop"
  ];

  const stats = [
    { number: "99.9%", label: "Uptime Guarantee", icon: "Activity" },
    { number: "24/7", label: "Expert Support", icon: "Headphones" },
    { number: "5 Years", label: "Average Partnership", icon: "Calendar" },
    { number: "Zero", label: "Data Breaches", icon: "ShieldCheck" }
  ];

  return (
    <section className="py-20 bg-white border-t border-border">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="ShieldCheck" size={16} />
            <span>Trusted & Secure</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Enterprise-Grade Security & Compliance
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Your data security is our top priority. We maintain the highest standards of security, compliance, and reliability.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon name={stat.icon} size={24} className="text-primary" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">{stat.number}</div>
              <div className="text-muted-foreground font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Industry Certifications & Partnerships
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert) => (
              <div key={cert.id} className="text-center group">
                <div className="relative mb-4">
                  <div className="w-20 h-20 rounded-xl bg-muted/50 flex items-center justify-center mx-auto group-hover:bg-primary/10 transition-colors">
                    <Icon name={cert.icon} size={32} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-success flex items-center justify-center">
                    <Icon name="Check" size={14} color="white" />
                  </div>
                </div>
                <div className="font-semibold text-foreground mb-1">{cert.name}</div>
                <div className="text-sm text-muted-foreground">{cert.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Security Features */}
        <div className="bg-gradient-to-r from-muted/30 to-primary/5 rounded-2xl p-8 lg:p-12 mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Security Features
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 rounded-full bg-white shadow-soft flex items-center justify-center mx-auto mb-4">
                  <Icon name={feature.icon} size={20} className="text-primary" />
                </div>
                <h4 className="font-semibold text-foreground mb-2">{feature.title}</h4>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Client Logos */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-foreground mb-8">
            Trusted by Leading Organizations Worldwide
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 items-center">
            {clientLogos.map((logo, index) => (
              <div key={index} className="flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity">
                <Image
                  src={logo}
                  alt={`Client ${index + 1}`}
                  className="h-8 object-contain filter grayscale hover:grayscale-0 transition-all"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Security Badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-4 bg-white border border-border rounded-xl px-6 py-4 shadow-soft">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="ShieldCheck" size={24} className="text-success" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-foreground">Secured by RCEG AI</div>
              <div className="text-sm text-muted-foreground">Enterprise-grade security & compliance</div>
            </div>
            <div className="text-success">
              <Icon name="Check" size={20} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSignals;